import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";
import { Search, Palette, Code, Rocket } from "lucide-react";

const STEPS = [
  { num: "01", title: "Discovery", desc: "We learn your business, goals, and audience to define the right approach.", icon: Search },
  { num: "02", title: "Design", desc: "Wireframes and visual designs tailored to your brand and conversion goals.", icon: Palette },
  { num: "03", title: "Develop", desc: "Clean, fast, responsive builds on your chosen platform, delivered on time.", icon: Code },
  { num: "04", title: "Launch & Grow", desc: "Go live with confidence. We provide support, optimization, and ongoing growth.", icon: Rocket },
];

export function HowWeWork() {
  const { lang } = useLanguage();
  const t = (key: string) => getTranslation(key, lang);

  return (
    <section className="py-10 lg:py-24 bg-secondary/40">
      <div className="container px-6">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          {/* Left — sticky heading */}
          <div className="lg:sticky lg:top-28">
            <motion.p
              className="text-sm uppercase tracking-[0.15em] text-primary font-medium mb-4"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              {t("Our Process")}
            </motion.p>
            <motion.h2
              className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-balance"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.08 }}
            >
              {t("From Brief to Launch in")} <span className="text-gradient">{t("4 Steps")}</span>
            </motion.h2>
          </div>

          {/* Right — vertical timeline */}
          <div className="relative">
            {/* Connecting line — vertical */}
            <div className="absolute top-0 left-6 w-px border-l-2 border-dashed border-primary/20 z-0" style={{ height: 'calc(100% - 4rem)' }} />

            <div className="space-y-16">
              {STEPS.map((step, i) => (
                <motion.div
                  key={step.num}
                  className="relative z-10 flex items-start gap-6"
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-button">
                    <step.icon className="h-[18px] w-[18px] text-primary-foreground" strokeWidth={1.5} />
                  </div>
                  <div className="pt-2">
                    <h3 className="text-lg font-sans font-medium text-foreground mb-1.5">
                      {t(step.title)}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed max-w-md">
                      {t(step.desc)}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
